import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoWordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Countdown, formatDeadline } from "@/components/Countdown";
import { track } from "@/lib/analytics";

export function SiteHeader({ config }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const settings = config?.settings || {};
  const galleryOn = !!settings.public_gallery_enabled;
  const submissionsOpen = config?.submissions_open !== false;

  useEffect(() => setOpen(false), [location.pathname]);

  const nav = [
    { label: "Utama", to: "/" },
    { label: "Cara Berfungsi", to: "/#cara-ia-berfungsi" },
    { label: "Ketelusan", to: "/cara-pemilihan" },
    { label: "FAQ", to: "/faq" }, ...(galleryOn ? [{ label: "Soalan Rakyat", to: "/soalan-rakyat" }] : []),
  ];

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="ss-brandline" aria-hidden="true" />

      <div className="border-b border-border bg-[hsl(var(--card))]/95 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--card))]/85">
        <div className="ss-container flex h-16 items-center justify-between gap-4">
          <Link to="/" title="Satu Soalan untuk PMX, ke laman utama" data-testid="header-logo-link">
            <LogoWordmark />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigasi utama">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-xl px-3 py-2 text-sm font-semibold transition-colors hover:bg-[hsl(var(--secondary))] ${
                    isActive && item.to !== "/#cara-ia-berfungsi"
                      ? "text-[hsl(var(--ss-accent))]"
                      : "text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              asChild
              className="hidden rounded-xl bg-[hsl(var(--ss-accent))] font-semibold text-white hover:bg-[hsl(var(--ss-accent))]/85 sm:inline-flex"
              data-testid="header-cta-hantar-soalan"
            >
              <Link to="/hantar-soalan" onClick={() => track("hero_cta_clicked")}>
                Hantar Soalan
              </Link>
            </Button>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="rounded-xl border border-border bg-card p-2 text-foreground lg:hidden"
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? "Tutup menu" : "Buka menu"}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            id="mobile-menu"
            className="border-t border-border bg-card lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="ss-container flex flex-col gap-1 py-3">
              {nav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground hover:bg-[hsl(var(--secondary))]"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              ))}
              <Button
                asChild
                className="mt-2 rounded-xl bg-[hsl(var(--ss-accent))] font-semibold text-white hover:bg-[hsl(var(--ss-accent))]/85"
                data-testid="mobile-cta-hantar-soalan"
              >
                <Link to="/hantar-soalan">Hantar Soalan Saya</Link>
              </Button>
            </div>
          </div>
        )}
      </div>

      <div
        className="border-b border-border bg-[hsl(var(--secondary))]"
        data-testid="deadline-sticky-bar"
      >
        <div className="ss-container flex min-h-[68px] flex-wrap items-center justify-center sm:min-h-[40px] gap-x-2 gap-y-1 py-2 text-center text-[12px] sm:text-[13px]">
          {submissionsOpen ? (
            <>
              <span className="font-bold text-[hsl(var(--ss-accent))]">
                Penghantaran soalan ditutup pada
              </span>
              <span className="font-medium text-foreground" data-testid="deadline-text">
                {settings.deadline_at ? `${formatDeadline(settings.deadline_at)}.` : ""}
              </span>
              {settings.deadline_at && (
                <span
                  className="rounded-full bg-[hsl(var(--ss-panel))] px-2.5 py-0.5 text-[11px] font-bold text-white sm:text-[12px]"
                  data-testid="deadline-bar-countdown"
                >
                  <Countdown deadlineIso={settings.deadline_at} variant="inline" />
                </span>
              )}
            </>
          ) : (
            <span
              data-testid="submissions-closed-bar"
              className="font-bold text-foreground"
            >
              Penghantaran telah ditutup. Terima kasih kepada semua peserta.
            </span>
          )}
        </div>
      </div>

    </header>
  );
}
